import { i18n } from '@lingui/core';
import { isSameYear } from 'date-fns';
import { MISSING_VALUE } from '../string/string';
import {
  CustomDateFormats,
  extractDate,
  isSameDay,
  isToday,
  isTomorrow,
  isYesterday,
  type MaybeDate,
  NoddiDateFormats,
  noddiFormatDate
} from './date';

/**
 * One display format per role, so a date reads the same on every screen:
 *  - list row: "Today" / "Yesterday" / "Tomorrow", else "Tue 22 Sep" (year only when it is not this year)
 *  - date and time: "Yesterday, 07:01" / "Tue 22 Sep, 07:01"
 *  - heading: "Sunday 27 September"
 *  - calendar tile: { weekday: "Sun", day: "27", month: "Sep" }
 *  - time range: "13:00–16:58"; across midnight "Thu 24 Sep, 22:00 – Fri 25 Sep, 06:00"
 *  - date range: "Tue 22 Sep – Thu 24 Sep"
 *  - duration (from seconds): "8h 1m" / "8h" / "45m"
 *  - missing: MISSING_VALUE ("—") from every formatter
 * Times alone use `NoddiDateFormats.TIME`. The words and month names follow the active locale.
 * Clock times a worker and a manager must read alike: pass `businessTime(value)`.
 * Ranges use an en dash, closed up between bare values and spaced when a side holds a space.
 */

type DisplayLocale = 'en' | 'nb' | 'se';

const RELATIVE_DAYS: Record<DisplayLocale, { today: string; yesterday: string; tomorrow: string }> = {
  en: { today: 'Today', yesterday: 'Yesterday', tomorrow: 'Tomorrow' },
  nb: { today: 'I dag', yesterday: 'I går', tomorrow: 'I morgen' },
  se: { today: 'I dag', yesterday: 'I går', tomorrow: 'I morgon' }
};

const activeLocale = (): DisplayLocale => (i18n.locale === 'en' || i18n.locale === 'se' ? i18n.locale : 'nb');

const LOCALE_TAGS: Record<DisplayLocale, string> = { en: 'en-GB', nb: 'nb-NO', se: 'sv-SE' };

/** The active display locale as a BCP 47 tag, for components that format with Intl (react-aria). */
export const activeLocaleTag = (): string => LOCALE_TAGS[activeLocale()];

/**
 * Sets the locale dates format in, for an app with no Lingui catalog (the worker app, partner). It activates
 * an empty catalog, so an app that loads its own must never call it.
 */
export const activateDateLocale = (locale: DisplayLocale) => i18n.loadAndActivate({ locale, messages: {} });

interface Options {
  /** Lowercases a relative day in the middle of a sentence ("Since yesterday, 07:01"). */
  inSentence?: boolean;
}

const relativeWord = (date: NonNullable<MaybeDate>): string | null => {
  const words = RELATIVE_DAYS[activeLocale()];
  if (isToday(date)) {
    return words.today;
  }
  if (isYesterday(date)) {
    return words.yesterday;
  }
  if (isTomorrow(date)) {
    return words.tomorrow;
  }
  return null;
};

export const formatListDate = (date: MaybeDate, options: Options = {}): string => {
  const parsed = extractDate(date);
  if (!parsed) {
    return MISSING_VALUE;
  }
  const relative = relativeWord(parsed);
  if (relative) {
    return options.inSentence ? relative.toLowerCase() : relative;
  }
  const sameYear = isSameYear(parsed, new Date());
  return noddiFormatDate(
    date,
    sameYear ? CustomDateFormats.SHORT_WEEKDAY_DAY_SHORT_MONTH : CustomDateFormats.SHORT_WEEKDAY_DAY_SHORT_MONTH_YEAR
  );
};

export const formatDisplayDateTime = (date: MaybeDate, options: Options = {}): string =>
  extractDate(date)
    ? `${formatListDate(date, options)}, ${noddiFormatDate(date, NoddiDateFormats.TIME)}`
    : MISSING_VALUE;

export const formatHeadingDate = (date: MaybeDate): string =>
  extractDate(date) ? noddiFormatDate(date, CustomDateFormats.FULL_WEEKDAY_DAY_MONTH) : MISSING_VALUE;

/**
 * The parts of a calendar-style date tile, stacked by the caller. The month carries a short year when it is not
 * this year ("May ’23"): it shares the tile's narrow last line, where "May 2023" does not fit.
 */
export const formatCalendarTileDate = (date: MaybeDate): { weekday: string; day: string; month: string } => {
  const extracted = extractDate(date);
  if (!extracted) {
    return { weekday: MISSING_VALUE, day: MISSING_VALUE, month: MISSING_VALUE };
  }
  const month = noddiFormatDate(date, CustomDateFormats.SHORT_MONTH);
  return {
    weekday: noddiFormatDate(date, CustomDateFormats.SHORT_WEEKDAY),
    day: noddiFormatDate(date, CustomDateFormats.DAY_OF_MONTH),
    month: isSameYear(extracted, new Date())
      ? month
      : `${month} ’${noddiFormatDate(date, CustomDateFormats.FULL_YEAR).slice(-2)}`
  };
};

export const formatTimeRange = (start: MaybeDate, end: MaybeDate): string => {
  const startDate = extractDate(start);
  const endDate = extractDate(end);
  if (!startDate || !endDate) {
    return MISSING_VALUE;
  }
  if (!isSameDay(startDate, endDate)) {
    return `${formatDisplayDateTime(start)} – ${formatDisplayDateTime(end)}`;
  }
  return `${noddiFormatDate(start, NoddiDateFormats.TIME)}–${noddiFormatDate(end, NoddiDateFormats.TIME)}`;
};

export const formatDateRange = (start: MaybeDate, end: MaybeDate): string => {
  const startDate = extractDate(start);
  const endDate = extractDate(end);
  if (!startDate || !endDate) {
    return MISSING_VALUE;
  }
  return isSameDay(startDate, endDate) ? formatListDate(start) : `${formatListDate(start)} – ${formatListDate(end)}`;
};

/** Hours and minutes, never rolled into days: a 73-hour overtime balance reads "73h 49m". */
export const formatDuration = (seconds: number): string => {
  const wholeSeconds = Math.max(0, Math.round(seconds));
  if (wholeSeconds > 0 && wholeSeconds < 60) {
    return '<1m';
  }
  const totalMinutes = Math.floor(wholeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return `${minutes}m`;
  }
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};
